import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Store, select } from '@ngrx/store';
import { map, take } from 'rxjs/operators';

import { Observable } from 'rxjs';
import { AppFeatures } from '../app.features';
import { UserAccessLevel } from '../services/user.service';
import { UserState } from '../state/auth.reducer';

/**
 * Per-route access gate. Reads the requirement from route data:
 * - `minAccessLevel`: the UserAccessLevel the user must reach (defaults to
 *   SYSTEM_ADMIN when absent).
 * - `requiredFeature`: an AppFeatures component, or an any-of list of them,
 *   the user must hold in `feature_components`; `featureBypassAccessLevel`
 *   admits users at or above that level without the feature. This is the
 *   shape of the backend FeatureComponentGate and of MenuItem.permission /
 *   permission_bypass_access_level, so a route refuses exactly what the menu
 *   hides.
 * Compares against `state.user` from the NgRx auth state - the same source
 * SysadminGuard uses. Redirects to '/auth' when unauthenticated and to '/'
 * when the requirement is not met. The companion to SysadminGuard's
 * module-load gate: SysadminGuard admits GLOBAL_ADMIN, so each sysadmin child
 * re-asserts its own minimum here.
 */
@Injectable({
  providedIn: 'root',
})
export class MinAccessLevelGuard implements CanActivate {
  constructor(
    private store: Store<{ user: UserState }>,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    const data = route.data ?? {};
    const required =
      (data['minAccessLevel'] as UserAccessLevel | undefined) ??
      UserAccessLevel.SYSTEM_ADMIN;
    const feature = data['requiredFeature'] as
      | AppFeatures
      | AppFeatures[]
      | undefined;
    const requiredFeatures =
      feature === undefined ? [] : Array.isArray(feature) ? feature : [feature];
    const featureBypass = data['featureBypassAccessLevel'] as
      | UserAccessLevel
      | undefined;
    return this.store.pipe(
      select((s) => s.user),
      take(1),
      map((userState) => {
        if (!userState.isAuthChecked || !userState.isAuthenticated) {
          this.router.navigate(['/auth']);
          return false;
        }
        const accessLevel =
          userState.user?.access_level_id ?? UserAccessLevel.GUEST;
        if (accessLevel < required) {
          this.router.navigate(['/']);
          return false;
        }
        if (requiredFeatures.length > 0) {
          const components = userState.user?.feature_components ?? {};
          const hasFeature = requiredFeatures.some((f) => components[f]);
          const bypassed =
            featureBypass !== undefined && accessLevel >= featureBypass;
          if (!hasFeature && !bypassed) {
            this.router.navigate(['/']);
            return false;
          }
        }
        return true;
      }),
    );
  }
}
